import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { BillableServiceService } from 'src/app/features/admin-portal/modules/billable-services/services/billable-service.service';
import { ChargeTypeLabels, ServiceCategory, ServiceCategoryLabels } from 'src/app/shared/enums/enums';

export interface BillableServiceSearchResult {
  serviceId: number;
  serviceName: string;
  chargeId: number;
  chargeName: string;
  category: ServiceCategory;
  price: number;
  taxRate?: number;
  description?: string;
  displayName: string; // "Service Name - Name"
  appId?: number | null;
  appName?: string | null;
}

@Component({
  selector: 'app-billable-service-search',
  templateUrl: './billable-service-search.component.html',
  styleUrls: ['./billable-service-search.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BillableServiceSearchComponent),
      multi: true
    }
  ]
})
export class BillableServiceSearchComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder = 'Search service...';
  @Input() minSearchLength = 2;
  @Input() disabled = false;
  @Input() defaultTaxRate = 10;
  @Input() set initialValue(value: BillableServiceSearchResult | null) {
    if (value) {
      this.writeValue(value);
    }
  }
  @Output() serviceSelected = new EventEmitter<BillableServiceSearchResult>();
  @Output() serviceCleared = new EventEmitter<void>();

  searchText = '';
  filteredServices: BillableServiceSearchResult[] = [];
  isSearching = false;
  showDropdown = false;
  selectedService: BillableServiceSearchResult | null = null;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor(private billableServiceService: BillableServiceService) {}

  ngOnInit(): void {
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchText => {
      this.searchServices(searchText);
    });
  }

  onSearchInput(searchText: string): void {
    this.searchText = searchText;

    if (!this.selectedService) {
      this.onChange(null);
    }

    if (searchText.length >= this.minSearchLength) {
      this.showDropdown = true;
      this.searchSubject.next(searchText);
    } else {
      this.showDropdown = false;
      this.filteredServices = [];
    }
  }

  onFocus(): void {
    this.onTouched();
    if (this.searchText.length >= this.minSearchLength) {
      this.showDropdown = true;
      if (this.filteredServices.length === 0) {
        this.searchSubject.next(this.searchText);
      }
    }
  }

  searchServices(searchText: string): void {
    this.isSearching = true;

    this.billableServiceService.fetchWithSearch(searchText)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isSearching = false)
      )
      .subscribe({
        next: (services) => {
          // Flatten services into individual charges
          this.filteredServices = [];
          services.forEach(service => {
            // If service has charges, create an entry for each charge
            if (service.billableServiceCharges && service.billableServiceCharges.length > 0) {
              service.billableServiceCharges.forEach(charge => {
                this.filteredServices.push({
                  serviceId: service.id,
                  serviceName: service.name,
                  chargeId: charge.id,
                  chargeName: charge.name,
                  category: service.category,
                  price: charge.value,
                  taxRate: this.defaultTaxRate,
                  description: charge.description || service.description || undefined,
                  displayName: `${service.name} - ${charge.name} (${ChargeTypeLabels[charge.type]})`,
                  appId: service.appId,
                  appName: service.app?.name || null
                });
              });
            } else {
              // If no charges, create a single entry with the service
              this.filteredServices.push({
                serviceId: service.id,
                serviceName: service.name,
                chargeId: 0, // No charge ID
                chargeName: '',
                category: service.category,
                price: 0,
                taxRate: this.defaultTaxRate,
                description: service.description || undefined,
                displayName: service.name,
                appId: service.appId,
                appName: service.app?.name || null
              });
            }
          });
        },
        error: (error) => {
          console.error('Error searching billable services:', error);
          this.filteredServices = [];
        }
      });
  }

  selectService(service: BillableServiceSearchResult): void {
    this.selectedService = service;
    this.searchText = service.displayName;
    this.showDropdown = false;
    this.onChange(service);
    this.serviceSelected.emit(service);
  }

  clearSelection(): void {
    this.selectedService = null;
    this.searchText = '';
    this.filteredServices = [];
    this.showDropdown = false;
    this.onChange(null);
    this.serviceCleared.emit();
  }

  getCategoryLabel(category: ServiceCategory): string {
    return ServiceCategoryLabels[category] || 'Unknown';
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  // ControlValueAccessor implementation
  writeValue(value: BillableServiceSearchResult | null): void {
    if (value) {
      this.selectedService = value;
      this.searchText = value.displayName;
    } else {
      this.selectedService = null;
      this.searchText = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
